import React from 'react'
import SortableTable, {ColumnConfig} from '../SortableTable'
import {useSelector} from 'react-redux'
import HighlightText from '../HighlightText'
import PropTypes from 'prop-types'
import EmptyStateIconAndMessage from '../EmptyStateIconAndMessage'
import RXRIcon from '../RXRIcon'
import {CATEGORY_USER_TYPE} from '../Settings/SettingsConstants'
import {getBuildingLabelsColumnConfig} from './BuildingLabelsColumnConfig'
import {selectBuildingsByActiveOrganization} from '../../reducers/selectors'
import {PERMISSION_GROUP_GLOBAL_ADMIN, PERMISSION_GROUP_FORMER_USER} from '../../constants/ModelConstants'
import {isGlobalAdminUserType} from '../../Utils/globalAdminUtils'

function ListUsersTable(props) {
  const staffLookup = useSelector(state => state.GlobalAdmin.staffLookup)
  const buildingsLookup = useSelector(state => state.GlobalAdmin.buildingsLookup)
  const buildingsInOrg = useSelector(selectBuildingsByActiveOrganization)
  const staffByBuilding = useSelector(state => state.GlobalAdmin.staffByBuilding)
  const buildingsByStaff = Object.entries(staffByBuilding).reduce((acc, [buildingId, staffIds]) => {
    staffIds.forEach(sId => {
      if (!acc[sId]) {
        acc[sId] = []
      }
      acc[sId].push(buildingsLookup[buildingId])
    })
    return acc
  }, {})

  const lowerFilter = props.filterTerm ? props.filterTerm.toLowerCase() : ''
  const dataToRender = Object.values(staffLookup)
    .map(s => {
      return {
        staffId: s.id,
        name: s.displayName,
        isDisabled: s.isDisabled,
        permissionGroup: s.permissionGroup,
        userType: CATEGORY_USER_TYPE[s.permissionGroup] || 'Unknown',
        buildings: buildingsByStaff[s.id] || [],
        buildingsLabel: (buildingsByStaff[s.id] || []).map(b => b.displayName).join(', '),
      }
    })
    .filter(item => {
      // filter by search term
      if (!props.filterTerm) {
        return true
      }

      return (
        item.name.toLowerCase().includes(lowerFilter) ||
        item.userType.toLowerCase().includes(lowerFilter) ||
        item.buildingsLabel.toLowerCase().includes(lowerFilter)
      )
    })
    .filter(item => {
      // filter by buildings
      if (!props.filterBuildings || props.filterBuildings.length === buildingsInOrg.length) {
        return true
      }

      return item.buildings.some(b => props.filterBuildings.includes(b.id))
    })

  const hasTermFilter = !!props.filterTerm
  const hasBuildingFilter = props.filterBuildings && props.filterBuildings.length !== buildingsInOrg.length

  // sort buildings so that matches appear first
  dataToRender.forEach(item => {
    if (!hasTermFilter && !hasBuildingFilter) {
      return
    }

    item.buildings.sort((a, b) =>
      (hasTermFilter && a.displayName.toLowerCase().includes(lowerFilter)) ||
      (hasBuildingFilter && props.filterBuildings && props.filterBuildings.includes(a.id))
        ? -1
        : (hasTermFilter && b.displayName.toLowerCase().includes(lowerFilter)) ||
          (hasBuildingFilter && props.filterBuildings && props.filterBuildings.includes(b.id))
        ? 1
        : 0,
    )
  })

  const columns = [
    new ColumnConfig({
      title: 'Name',
      renderPrimitive: item => item.name,
      render: (r, col) => {
        return (
          <div>
            <HighlightText search={props.filterTerm}>{col.renderPrimitive(r)}</HighlightText>
          </div>
        )
      },
      comparator: ColumnConfig.simpleComparatorGenerator('name'),
    }),

    new ColumnConfig({
      title: 'User type',
      renderPrimitive: g => {
        if (isGlobalAdminUserType(props.activeOrganization, {id: g.staffId, permissionGroup: g.permissionGroup})) {
          return CATEGORY_USER_TYPE[PERMISSION_GROUP_GLOBAL_ADMIN]
        }
        return g.isDisabled
          ? CATEGORY_USER_TYPE[PERMISSION_GROUP_FORMER_USER]
          : g.permissionGroup
          ? CATEGORY_USER_TYPE[g.permissionGroup]
          : 'N/A'
      },
      render: (r, col) => {
        return (
          <div>
            <HighlightText search={props.filterTerm}>{col.renderPrimitive(r)}</HighlightText>
          </div>
        )
      },
      comparator: ColumnConfig.primitiveComparator,
    }),

    getBuildingLabelsColumnConfig(props.filterTerm),
  ]

  if (dataToRender.length > 0) {
    return <SortableTable data={dataToRender} columns={columns} getKey={d => d.staffId} onClickRow={r => props.onClickUser(r.staffId)} />
  }

  // Show appropriate empty state
  if (hasTermFilter || hasBuildingFilter) {
    return <EmptyStateIconAndMessage message={'No users match the selected filter(s)'} icon={RXRIcon.USER_MANAGEMENT} />
  }

  return <EmptyStateIconAndMessage message={'No users in org'} icon={RXRIcon.USER_MANAGEMENT} />
}

ListUsersTable.propTypes = {
  filterTerm: PropTypes.string,
  filterBuildings: PropTypes.array,
  onClickUser: PropTypes.func,
}

export default ListUsersTable
